// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Mail, Cloud, CheckCircle, AlertTriangle, RefreshCw, Settings, Download, Upload, Eye, MoreVertical } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const IntegrareOffice360 = () => {
  const [syncStatus, setSyncStatus] = useState('active'); // active, paused, error
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [autoSync, setAutoSync] = useState(true);

  // Auto-refresh sync time every minute
  useEffect(() => {
    if (!autoSync) return;
    const interval = setInterval(() => {
      setLastSyncTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, [autoSync]);

  // Mock data pentru integrarea Office 365
  const office365Data = {
    overview: {
      connectedServices: 8,
      activeSyncs: 12,
      dataTransferred: '2.4 GB',
      lastSync: lastSyncTime.toLocaleString('ro-RO'),
      syncErrors: 2,
      syncSuccess: 98.3
    },
    integrations: [
      {
        id: 'INT-001',
        service: 'Excel Online',
        type: 'Spreadsheet',
        status: 'Connected',
        lastSync: '2024-03-15 14:45:00',
        syncType: 'Bi-directional',
        files: ['Balanta_Verificare.xlsx', 'Registru_Casa.xlsx', 'Facturi_2024.xlsx'],
        dataVolume: '450 MB',
        syncFrequency: 'Real-time',
        errors: 0,
        successRate: 100,
        nextSync: 'Continuous',
        configuration: {
          autoSync: true,
          conflictResolution: 'Server wins',
          backupEnabled: true,
          versionControl: true
        }
      },
      {
        id: 'INT-002',
        service: 'Outlook Mail',
        type: 'Email',
        status: 'Connected',
        lastSync: '2024-03-15 14:40:00',
        syncType: 'Import only',
        files: ['Facturi_Email/*.eml', 'Contracte/*.pdf', 'Extracte_Bancare/*.pdf'],
        dataVolume: '1.2 GB',
        syncFrequency: 'Every 15 minutes',
        errors: 1,
        successRate: 97.8,
        nextSync: '2024-03-15 15:00:00',
        configuration: {
          autoSync: true,
          folderFilter: ['Facturi', 'Contracte', 'Banca'],
          attachmentSync: true,
          archiveProcessed: true
        }
      },
      {
        id: 'INT-003',
        service: 'SharePoint Sites',
        type: 'Document Library',
        status: 'Connected',
        lastSync: '2024-03-15 14:30:00',
        syncType: 'Bi-directional',
        files: ['Documente_Contabile/*', 'Rapoarte/*', 'Archive/*'],
        dataVolume: '890 MB',
        syncFrequency: 'Hourly',
        errors: 0,
        successRate: 99.2,
        nextSync: '2024-03-15 15:30:00',
        configuration: {
          autoSync: true,
          permissionSync: true,
          metadataSync: true,
          versionHistory: true
        }
      },
      {
        id: 'INT-004',
        service: 'OneDrive Business',
        type: 'Cloud Storage',
        status: 'Syncing',
        lastSync: '2024-03-15 14:47:30',
        syncType: 'Bi-directional',
        files: ['Personal_Backups/*', 'Working_Files/*'],
        dataVolume: '1.8 GB',
        syncFrequency: 'Real-time',
        errors: 1,
        successRate: 98.9,
        nextSync: 'Continuous',
        configuration: {
          autoSync: true,
          selectiveSync: true,
          offlineAccess: true,
          encryptionEnabled: true
        }
      }
    ],
    workflows: [
      {
        id: 'WF-001',
        name: 'Procesare Facturi Email',
        description: 'Extrage automat facturile din Outlook și le încarcă în sistem',
        trigger: 'New email with attachment',
        status: 'Active',
        executions: 156,
        successRate: 96.8,
        lastRun: '2024-03-15 14:32:00',
        steps: [
          'Monitorizează inbox-ul pentru emailuri noi',
          'Identifică attachmenturile PDF cu facturi',
          'Extrage datele folosind OCR',
          'Validează și încarcă în sistemul contabil',
          'Arhivează emailul în folderul "Processed"'
        ]
      },
      {
        id: 'WF-002',
        name: 'Sincronizare Balanta Excel',
        description: 'Actualizează automat balanta de verificare în Excel Online',
        trigger: 'New accounting entry',
        status: 'Active',
        executions: 1247,
        successRate: 99.2,
        lastRun: '2024-03-15 14:45:00',
        steps: [
          'Detectează modificări în registrele contabile',
          'Calculează noile solduri ale conturilor',
          'Actualizează Excel Online cu noile date',
          'Creează backup-ul versiunii anterioare',
          'Notifică echipa contabilă despre actualizări'
        ]
      },
      {
        id: 'WF-003',
        name: 'Raportare SharePoint',
        description: 'Generează și publică rapoartele financiare în SharePoint',
        trigger: 'End of month',
        status: 'Scheduled',
        executions: 12,
        successRate: 100,
        lastRun: '2024-03-01 08:00:00',
        steps: [
          'Generează rapoartele financiare lunare',
          'Formatează documentele pentru publicare',
          'Încarcă rapoartele în SharePoint library',
          'Setează permisiunile de acces corespunzătoare',
          'Trimite notificări către stakeholders'
        ]
      }
    ],
    syncLogs: [
      {
        id: 'LOG-001',
        timestamp: '2024-03-15 14:45:00',
        service: 'Excel Online',
        action: 'File Updated',
        file: 'Balanta_Verificare.xlsx',
        status: 'Success',
        details: '247 rows updated, 0 conflicts',
        duration: '2.3s'
      },
      {
        id: 'LOG-002',
        timestamp: '2024-03-15 14:40:00',
        service: 'Outlook Mail',
        action: 'Email Processed',
        file: 'Factura_TechCorp_156.pdf',
        status: 'Success',
        details: 'Invoice extracted and uploaded',
        duration: '5.7s'
      },
      {
        id: 'LOG-003',
        timestamp: '2024-03-15 14:38:00',
        service: 'OneDrive Business',
        action: 'File Sync',
        file: 'Backup_Daily_20240315.zip',
        status: 'Error',
        details: 'File size exceeds limit (>100MB)',
        duration: '1.2s'
      }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Connected': return 'success';
      case 'Syncing': return 'warning';
      case 'Error': return 'error';
      case 'Paused': return 'info';
      case 'Success': return 'success';
      case 'Active': return 'success';
      case 'Scheduled': return 'info';
      default: return 'default';
    }
  };

  const getSyncTypeColor = (type) => {
    switch (type) {
      case 'Bi-directional': return 'info';
      case 'Import only': return 'warning';
      case 'Export only': return 'success';
      default: return 'default';
    }
  };

  const integrationColumns = [
    { 
      header: 'Serviciu', 
      key: 'service',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          {row.type === 'Spreadsheet' && <FileSpreadsheet className="w-5 h-5 text-green-600" />}
          {row.type === 'Email' && <Mail className="w-5 h-5 text-blue-600" />}
          {row.type === 'Document Library' && <Cloud className="w-5 h-5 text-purple-600" />}
          {row.type === 'Cloud Storage' && <Cloud className="w-5 h-5 text-orange-600" />}
          <div>
            <div className="font-medium text-gray-900 dark:text-white">{value}</div>
            <div className="text-xs text-gray-500">{row.id} • {row.type}</div>
          </div>
        </div>
      )
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Tip Sync', 
      key: 'syncType',
      render: (value) => <Badge variant={getSyncTypeColor(value)} className="text-xs">{value}</Badge>
    },
    { 
      header: 'Volume Date', 
      key: 'dataVolume',
      render: (value, row) => (
        <div className="text-center">
          <div className="font-semibold">{value}</div>
          <div className="text-xs text-gray-500">{row.files.length} fișiere</div>
        </div>
      )
    },
    { 
      header: 'Frecvența', 
      key: 'syncFrequency'
    },
    { 
      header: 'Success Rate', 
      key: 'successRate',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 95 ? 'bg-green-500' : value > 90 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Erori', 
      key: 'errors',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {value}
        </span>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedIntegration(row);
              setShowSyncModal(true);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm"
            title="Vezi Detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button className="text-gray-600 hover:text-gray-800 text-sm" title="Opțiuni">
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const workflowColumns = [
    { header: 'Workflow', key: 'name' },
    { header: 'Trigger', key: 'trigger' },
    { header: 'Status', key: 'status', render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge> },
    { header: 'Execuții', key: 'executions', render: (value) => value.toLocaleString() },
    { header: 'Success Rate', key: 'successRate', render: (value) => `${value}%` },
    { header: 'Ultima Rulare', key: 'lastRun' }
  ];

  const logColumns = [
    { header: 'Timestamp', key: 'timestamp' },
    { header: 'Serviciu', key: 'service' },
    { header: 'Acțiune', key: 'action' },
    { header: 'Fișier', key: 'file' },
    { header: 'Status', key: 'status', render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge> },
    { header: 'Detalii', key: 'details' },
    { header: 'Durată', key: 'duration' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Integrare Office 365</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sincronizare automată cu Excel, Outlook și SharePoint pentru fluxuri de lucru optimizate
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncStatus === 'active' ? 'animate-spin text-green-500' : 'text-gray-400'}`} />
            Ultima sincronizare: {office365Data.overview.lastSync}
            <span className="mx-2">•</span>
            <CheckCircle className="w-4 h-4 mr-1" />
            <span className="text-green-600">
              Success Rate: {office365Data.overview.syncSuccess}%
            </span>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            className={`btn-secondary ${autoSync ? 'bg-green-100 dark:bg-green-800' : 'bg-gray-100'}`}
            onClick={() => setAutoSync(!autoSync)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoSync ? 'animate-spin' : ''}`} />
            {autoSync ? 'Auto-Sync ON' : 'Auto-Sync OFF'}
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Logs
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowConfigModal(true)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurare
          </button>
        </div>
      </div>

      {/* Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <Card title="Servicii Conectate" subtitle="Office 365" icon={Cloud}>
          <div className="text-2xl font-bold text-primary-600">{office365Data.overview.connectedServices}</div>
          <div className="text-sm text-blue-600">Excel, Outlook, SharePoint</div>
        </Card>
        <Card title="Sincronizări Active" subtitle="În timp real" icon={RefreshCw}>
          <div className="text-2xl font-bold text-primary-600">{office365Data.overview.activeSyncs}</div>
          <div className="text-sm text-green-600">
            {syncStatus === 'active' && <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />}
            Continuu
          </div>
        </Card>
        <Card title="Date Transferate" subtitle="Această lună" icon={Upload}>
          <div className="text-2xl font-bold text-primary-600">{office365Data.overview.dataTransferred}</div>
          <div className="text-sm text-green-600">+32% vs luna trecută</div>
        </Card>
        <Card title="Success Rate" subtitle="Sincronizări" icon={CheckCircle}>
          <div className="text-2xl font-bold text-green-600">{office365Data.overview.syncSuccess}%</div>
          <div className="text-sm text-green-600">Target: 95%+</div>
        </Card>
        <Card title="Erori Sync" subtitle="Necesită atenție" icon={AlertTriangle}>
          <div className="text-2xl font-bold text-red-600">{office365Data.overview.syncErrors}</div>
          <div className="text-sm text-red-600">Rezolvare automată</div>
        </Card>
        <Card title="Status Sistem" subtitle="Conectivitate" icon={RefreshCw}>
          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${syncStatus === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <div className="text-lg font-bold text-green-600">Online</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Integrations Table */}
        <div className="lg:col-span-3">
          <Card title="Servicii Office 365" subtitle="Status sincronizare și configurații">
            <Table columns={integrationColumns} data={office365Data.integrations} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Statistici Sync" icon={RefreshCw}>
            <div className="space-y-4">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <FileSpreadsheet className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">Excel Online</span>
                </div>
                <div className="text-xs text-green-600 dark:text-green-400">
                  Real-time • 100% success • 3 fișiere
                </div>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Outlook Mail</span>
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  15min intervals • 97.8% success • 156 emailuri
                </div>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <Cloud className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800 dark:text-purple-200">SharePoint</span>
                </div>
                <div className="text-xs text-purple-600 dark:text-purple-400">
                  Hourly • 99.2% success • 45 documente
                </div>
              </div>
            </div>
          </Card>

          <Card title="Acțiuni Rapide">
            <div className="space-y-3">
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Sync Manual Complet</span>
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Test Conectivitate</span>
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Backup Configurații</span>
                  <Download className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Workflows */}
      <Card title="Workflow-uri Automatizate" subtitle="Procese automate pentru integrarea Office 365">
        <Table columns={workflowColumns} data={office365Data.workflows} />
      </Card>

      {/* Sync Logs */}
      <Card title="Jurnal Sincronizări" subtitle="Activitate recentă și status operațiuni">
        <Table columns={logColumns} data={office365Data.syncLogs} />
      </Card>

      {/* Integration Detail Modal */}
      <Modal
        isOpen={showSyncModal}
        onClose={() => {
          setShowSyncModal(false);
          setSelectedIntegration(null);
        }}
        title={`Detalii Integrare: ${selectedIntegration?.service}`}
        size="large"
      >
        {selectedIntegration && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span className="font-mono">{selectedIntegration.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Serviciu:</span>
                    <span>{selectedIntegration.service}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Tip:</span>
                    <span>{selectedIntegration.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <Badge variant={getStatusColor(selectedIntegration.status)}>
                      {selectedIntegration.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Tip Sync:</span>
                    <Badge variant={getSyncTypeColor(selectedIntegration.syncType)}>
                      {selectedIntegration.syncType}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Performance</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                      Success Rate
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {selectedIntegration.successRate}%
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                      Volume Date
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedIntegration.dataVolume}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Fișiere Sincronizate</h3>
              <div className="grid grid-cols-2 gap-2">
                {selectedIntegration.files.map((file, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                    <FileSpreadsheet className="w-4 h-4 text-gray-500" />
                    <span className="truncate">{file}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Configurații</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {Object.entries(selectedIntegration.configuration).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center">
                    <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}: </span>
                    {typeof value === 'boolean' ? (
                      <Badge variant={value ? 'success' : 'default'} className="text-xs">
                        {value ? 'Enabled' : 'Disabled'}
                      </Badge>
                    ) : (
                      <span className="font-medium">{value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Sincronizare</h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Frecvența:</span>
                    <span className="font-medium">{selectedIntegration.syncFrequency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ultima sync:</span>
                    <span className="font-medium">{selectedIntegration.lastSync}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Următoarea sync:</span>
                    <span className="font-medium">{selectedIntegration.nextSync}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Erori</h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Erori active:</span>
                    <span className={`font-medium ${selectedIntegration.errors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {selectedIntegration.errors}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Success rate:</span>
                    <span className="font-medium text-green-600">{selectedIntegration.successRate}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Pause Sync</button>
              <button className="btn-secondary">Force Sync Now</button>
              <button className="btn-primary">Edit Configuration</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configurare Office 365 Integration"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Setări Generale</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-sincronizare</span>
                  <p className="text-xs text-gray-500">Sincronizare automată pentru toate serviciile</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={autoSync}
                  onChange={(e) => setAutoSync(e.target.checked)}
                  className="rounded" 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Backup înainte de sync</span>
                  <p className="text-xs text-gray-500">Creează backup automat înainte de sincronizare</p>
                </div>
                <input type="checkbox" defaultChecked className="rounded" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notificări email</span>
                  <p className="text-xs text-gray-500">Alertă pentru erori și sincronizări importante</p>
                </div>
                <input type="checkbox" defaultChecked className="rounded" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Version control</span>
                  <p className="text-xs text-gray-500">Păstrează istoric versiuni pentru fișierele sincronizate</p>
                </div>
                <input type="checkbox" defaultChecked className="rounded" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Configurare Conexiuni</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Office 365 Tenant ID
                </label>
                <input
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  defaultValue="a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Application ID
                </label>
                <input
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  defaultValue="b2c3d4e5-f6g7-8901-bcde-f23456789012"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Servicii Active</h3>
            <div className="space-y-3">
              {office365Data.integrations.map((integration) => (
                <div key={integration.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input 
                      type="checkbox" 
                      defaultChecked={integration.status === 'Connected'} 
                      className="rounded" 
                    />
                    <div className="flex items-center space-x-2">
                      {integration.type === 'Spreadsheet' && <FileSpreadsheet className="w-4 h-4 text-green-600" />}
                      {integration.type === 'Email' && <Mail className="w-4 h-4 text-blue-600" />}
                      {integration.type === 'Document Library' && <Cloud className="w-4 h-4 text-purple-600" />}
                      {integration.type === 'Cloud Storage' && <Cloud className="w-4 h-4 text-orange-600" />}
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{integration.service}</div>
                        <div className="text-xs text-gray-500">{integration.syncFrequency}</div>
                      </div>
                    </div>
                  </div>
                  <Badge variant={getStatusColor(integration.status)} className="text-xs">
                    {integration.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Test All Connections
            </button>
            <button className="btn-primary">
              <Settings className="w-4 h-4 mr-2" />
              Salvează Configurația
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IntegrareOffice360;